/**
 * Copyright (c) [2024] Jangouts
 *
 * This software may be modified and distributed under the terms
 * of the MIT license.  See the LICENSE.txt file for details.
 */

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { PhoneOff, User } from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import { actionCreators as callActions } from "../../state/ducks/call";

/**
 * 呼出界面组件
 * 显示正在呼叫对方的界面
 */
const OutgoingCall = ({ callId, remoteUserId, remoteUsername, onCancel }) => {
  const dispatch = useDispatch();
  const [callDuration, setCallDuration] = useState(0);

  const currentCall = useSelector((state) => state.call.currentCall);

  // 模拟呼叫过程
  useEffect(() => {
    const timer = setInterval(() => {
      setCallDuration((prev) => prev + 1);
      // 模拟60秒后超时
      if (callDuration >= 60) {
        handleCancelCall();
      }
    }, 1000);

    return () => {
      console.log("清理函数被调用2");
      clearInterval(timer);
    };
  }, [callDuration, remoteUsername]);

  const handleCancelCall = () => {
    // 发送取消呼叫信令
    console.log(`Cancelling call to ${remoteUsername}`);
    // 更新Redux状态
    dispatch(callActions.endCall(callId));
    if (onCancel) {
      onCancel();
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
        {/* 对方信息 */}
        <div className="text-center mb-8">
          <div className="w-24 h-24 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
            <User size={48} className="text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {remoteUsername}
          </h2>
        </div>

        {/* 呼叫状态 */}
        <div className="text-center mb-8">
          <div className="text-3xl font-mono font-bold text-gray-800 mb-2">
            {formatDuration(callDuration)}
          </div>

          <div className="flex justify-center space-x-4 mb-4">
            <div className="flex items-center text-green-600">
              <div className="w-2 h-2 rounded-full bg-green-600 animate-ping mr-2"></div>
              <span className="font-medium">正在呼叫...</span>
            </div>
          </div>
          {/* 响铃动画 */}
          <div className="flex justify-center space-x-2">
            <div
              className="w-4 h-4 rounded-full bg-green-400 animate-bounce"
              style={{ animationDelay: "0ms" }}
            ></div>
            <div
              className="w-4 h-4 rounded-full bg-green-400 animate-bounce"
              style={{ animationDelay: "150ms" }}
            ></div>
            <div
              className="w-4 h-4 rounded-full bg-green-400 animate-bounce"
              style={{ animationDelay: "300ms" }}
            ></div>
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="flex justify-center">
          <button
            onClick={handleCancelCall}
            className="flex items-center justify-center px-8 py-4 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors shadow-lg"
          >
            <PhoneOff size={24} className="mr-2" />
            <span className="font-bold">取消呼叫</span>
          </button>
        </div>

        {/* 提示信息 */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>等待对方接听...</p>
          <p className="mt-1">如果长时间无人接听，呼叫将自动取消</p>
        </div>
        
      </div>
    </div>
  );
};

OutgoingCall.propTypes = {
  callId: PropTypes.string.isRequired,
  remoteUserId: PropTypes.number.isRequired,
  remoteUsername: PropTypes.string.isRequired,
  onCancel: PropTypes.func,
};

export default OutgoingCall;
